import {useQuery} from '@aboutyou/data-fetcher';
import {Coupon} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {useCallback} from 'react';
import {getCouponPromotionId} from './getCouponPromotionId';

export const useActivatedCouponState = (productId?: number) => {
  const {data, updateCache} = useQuery<{
    activatedCoupon?: Coupon | null;
  }>(
    `activated_coupon-${productId}`,
    () => ({
      activatedWebCoupon: undefined,
      activatedCoupon: undefined,
    }),
    {
      keepPreviousData: false,
    },
  );

  const {data: headlineData, updateCache: updateHeadlineCache} = useQuery<{
    headline?: string;
    unlockedCouponHeadline?: string;
  }>(
    `activated_coupon_headline-${productId}`,
    () => ({
      headline: undefined,
      unlockedCouponHeadline: undefined,
    }),
    {
      keepPreviousData: false,
    },
  );

  const setUnlockedCouponHeadline = useCallback(
    (unlockedCouponHeadline?: string) => {
      updateHeadlineCache({
        ...headlineData,
        unlockedCouponHeadline,
      });
    },
    [headlineData, updateHeadlineCache],
  );

  const setActivatedCoupon = useCallback(
    (coupon?: Coupon | null) => {
      updateCache({
        activatedCoupon: coupon,
      });
      setUnlockedCouponHeadline(undefined); // reset any unlocked CRM coupon headline when a new coupon is activated
    },
    [setUnlockedCouponHeadline, updateCache],
  );

  const setHeadline = useCallback(
    (headline: string) => {
      updateHeadlineCache({
        ...headlineData,
        headline,
      });
    },
    [updateHeadlineCache, headlineData],
  );

  const showHeadline =
    !data?.activatedCoupon ||
    (data.activatedCoupon.type?.$case === 'minimumOrderValueCoupon' &&
      data.activatedCoupon.type.minimumOrderValueCoupon.tiers.every(
        tier => tier.type?.$case !== 'active',
      ));

  return {
    activatedCoupon: data?.activatedCoupon,
    couponPromotionId: getCouponPromotionId(data?.activatedCoupon),
    setActivatedCoupon,
    headline: showHeadline ? headlineData?.headline : undefined,
    setHeadline,
    setUnlockedCouponHeadline,
    unlockedCouponHeadline: headlineData?.unlockedCouponHeadline,
  };
};
